import React, { useState } from 'react';
import { RequirementsSection } from './RequirementsSection';
import type { RequirementItem } from './types';
import requirementsIcon from '../../../assets/icons/requirements-icon.png';

interface RequirementsPanelProps {
  required: RequirementItem[];
  optional: RequirementItem[];
  onUpdate?: (required: RequirementItem[], optional: RequirementItem[]) => void;
  isLoading?: boolean;
  onCollapse?: () => void;
  /** When true, render requirements view-only (no buttons, no drag, no edit). */
  readOnly?: boolean;
  /**
   * Shown only for existing positions with NO requirements yet. When set AND required+optional
   * are both empty, a "Сгенерировать с помощью AI" button appears. Clicking it confirms via
   * an in-app modal, then calls this callback, which should return the new lists.
   */
  onRegenerateWithAi?: () => Promise<{ required: RequirementItem[]; optional: RequirementItem[] } | null>;
}

export function RequirementsPanel({ required, optional, onUpdate, isLoading, onCollapse, readOnly = false, onRegenerateWithAi }: RequirementsPanelProps) {
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenerateError, setRegenerateError] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const isEmpty = required.length === 0 && optional.length === 0;
  // Button is only offered when the position has no requirements yet — avoids destroying
  // manually-edited lists. Re-run from a freshly-empty state.
  const canRegenerate = !!onRegenerateWithAi && isEmpty;

  const runRegenerate = async () => {
    if (!onRegenerateWithAi || isRegenerating) return;
    setShowConfirm(false);
    setIsRegenerating(true);
    setRegenerateError(null);
    try {
      const result = await onRegenerateWithAi();
      if (!result) {
        setRegenerateError('Не удалось сгенерировать требования. Проверьте описание вакансии и попробуйте ещё раз.');
      }
    } catch (e: any) {
      setRegenerateError(e?.message || 'Ошибка генерации требований');
    } finally {
      setIsRegenerating(false);
    }
  };

  return (
    <>
      <div className="w-[420px] h-full flex flex-col bg-white rounded-[16px] border border-[#e2e4e9] overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 border-b border-[#e2e4e9] flex items-center justify-between shrink-0 gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <img src={requirementsIcon} alt="" className="w-6 h-6 shrink-0" />
            <div className="min-w-0">
              <div className="text-[14px] font-medium text-[#0a0d14] truncate">Требования к кандидату</div>
              <div className="text-[12px] text-[#868C98] truncate">Веса учитываются в скоринге и диалогах</div>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {canRegenerate && (
              <button
                onClick={() => setShowConfirm(true)}
                disabled={isRegenerating}
                className="px-2.5 h-8 flex items-center gap-1.5 rounded-lg border border-[#e2e4e9] text-[12px] font-medium text-[#0a0d14] hover:bg-[#f0f1f3] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                title="Сгенерировать требования с помощью AI"
              >
                {isRegenerating ? (
                  <span className="w-3.5 h-3.5 border-2 border-[#e2e4e9] border-t-[#525866] rounded-full animate-spin" />
                ) : (
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path d="M8 2L9.5 6.5L14 8L9.5 9.5L8 14L6.5 9.5L2 8L6.5 6.5L8 2Z" stroke="#525866" strokeWidth="1.3" strokeLinejoin="round" />
                  </svg>
                )}
                <span>{isRegenerating ? 'Генерация…' : 'AI'}</span>
              </button>
            )}
            {onCollapse && (
              <button
                onClick={onCollapse}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#f0f1f3] transition-colors"
                title="Свернуть"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M10 4L6 8L10 12" stroke="#525866" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {regenerateError && (
          <div className="px-4 py-2 bg-red-50 border-b border-red-200 text-[12px] text-red-700 shrink-0">
            {regenerateError}
          </div>
        )}

        {/* Content */}
        <div
          className="flex-1 overflow-y-auto custom-scrollbar"
          style={{ scrollbarWidth: 'thin', scrollbarColor: '#d4d7dc transparent' } as React.CSSProperties}
        >
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <div className="w-8 h-8 border-2 border-[#e2e4e9] border-t-[#525866] rounded-full animate-spin" />
              <p className="text-[13px] text-[#868C98]">Анализируем вакансию...</p>
            </div>
          ) : isEmpty ? (
            <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
              <img src={requirementsIcon} alt="" className="w-10 h-10 mb-3 opacity-40" />
              <p className="text-[14px] text-[#868C98]">Нет требований</p>
            </div>
          ) : (
            <div className="pt-3 pb-16">
              <RequirementsSection
                required={required}
                optional={optional}
                onUpdate={readOnly ? undefined : onUpdate}
                readOnly={readOnly}
              />
            </div>
          )}
        </div>
      </div>

      {showConfirm && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 ai-fade-in"
          onClick={() => setShowConfirm(false)}
        >
          <div
            className="bg-white rounded-[16px] shadow-xl p-6 w-[360px] ai-fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-[16px] font-medium text-[#0a0d14] mb-2">Сгенерировать требования?</p>
            <p className="text-[14px] text-[#525866] mb-5">
              AI составит список обязательных и дополнительных требований по описанию вакансии.
            </p>
            <div className="flex items-center gap-3 justify-end">
              <button
                className="px-4 py-2 text-[14px] font-medium text-[#525866] rounded-[10px] border border-[#e2e4e9] hover:bg-[#f9fafb] transition-colors"
                onClick={() => setShowConfirm(false)}
              >
                Отмена
              </button>
              <button
                className="px-4 py-2 text-[14px] font-medium text-white rounded-[10px] bg-[#375DFB] hover:bg-[#2e4cd4] transition-colors"
                onClick={runRegenerate}
              >
                Сгенерировать
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
